import { useEffect, useState } from "react";
import { AdminLayout } from "@/components/blog/AdminLayout";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ThumbsUp, ThumbsDown, Search, TrendingUp, MessageSquare } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";

interface FeedbackEntry {
  id: string;
  timestamp: string;
  user_question: string;
  ai_response: string;
  rating: number;
}

const AdminFeedback = () => {
  const [feedback, setFeedback] = useState<FeedbackEntry[]>([]);
  const [filteredFeedback, setFilteredFeedback] = useState<FeedbackEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [ratingFilter, setRatingFilter] = useState("all");
  const { toast } = useToast();

  useEffect(() => {
    fetchFeedback();
  }, []);

  useEffect(() => {
    filterFeedback();
  }, [feedback, searchQuery, ratingFilter]);

  const fetchFeedback = async () => {
    try {
      const { data, error } = await supabase
        .from("ai_chat_feedback")
        .select("*")
        .order("timestamp", { ascending: false });

      if (error) throw error;

      setFeedback(data || []);
    } catch (error) {
      console.error("Error fetching feedback:", error);
      toast({
        title: "Error",
        description: "Failed to load feedback data.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const filterFeedback = () => {
    let filtered = [...feedback];

    // Apply rating filter
    if (ratingFilter !== "all") {
      const rating = parseInt(ratingFilter);
      filtered = filtered.filter((item) => item.rating === rating);
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.user_question.toLowerCase().includes(query) ||
          item.ai_response.toLowerCase().includes(query)
      );
    }

    setFilteredFeedback(filtered);
  };

  const calculateStats = () => {
    const totalFeedback = feedback.length;
    const thumbsUp = feedback.filter((item) => item.rating === 1).length;
    const thumbsDown = feedback.filter((item) => item.rating === -1).length;
    const satisfactionRate = totalFeedback > 0 ? ((thumbsUp / totalFeedback) * 100).toFixed(1) : "0";

    return { totalFeedback, thumbsUp, thumbsDown, satisfactionRate };
  };

  const stats = calculateStats();

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Loading feedback data...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">AI Chat Feedback</h1>
          <p className="text-muted-foreground mt-2">
            Monitor and analyze user feedback from Denise's conversations
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-muted-foreground" />
                Total Feedback
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalFeedback}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <ThumbsUp className="w-4 h-4 text-green-600" />
                Thumbs Up
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{stats.thumbsUp}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <ThumbsDown className="w-4 h-4 text-red-600" />
                Thumbs Down
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">{stats.thumbsDown}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-blue-600" />
                Satisfaction Rate
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">{stats.satisfactionRate}%</div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle>Filter Feedback</CardTitle>
            <CardDescription>Search and filter feedback entries</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search questions or responses..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={ratingFilter} onValueChange={setRatingFilter}>
                <SelectTrigger className="w-full sm:w-[200px]">
                  <SelectValue placeholder="Filter by rating" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Ratings</SelectItem>
                  <SelectItem value="1">Thumbs Up Only</SelectItem>
                  <SelectItem value="-1">Thumbs Down Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Showing {filteredFeedback.length} of {feedback.length} entries
              </p>
              <Button variant="outline" size="sm" onClick={fetchFeedback}>
                Refresh Data
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Feedback List */}
        <div className="space-y-4">
          {filteredFeedback.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center">
                <p className="text-muted-foreground">No feedback entries found.</p>
              </CardContent>
            </Card>
          ) : (
            filteredFeedback.map((entry) => (
              <Card key={entry.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge
                          variant={entry.rating === 1 ? "default" : "destructive"}
                          className="flex items-center gap-1"
                        >
                          {entry.rating === 1 ? (
                            <>
                              <ThumbsUp className="w-3 h-3" />
                              Positive
                            </>
                          ) : (
                            <>
                              <ThumbsDown className="w-3 h-3" />
                              Negative
                            </>
                          )}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(entry.timestamp), "MMM d, yyyy 'at' h:mm a")}
                        </span>
                      </div>
                      <CardTitle className="text-base font-semibold">User Question</CardTitle>
                      <CardDescription className="mt-1">{entry.user_question}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <p className="text-sm font-medium">AI Response</p>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                      {entry.ai_response}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminFeedback;
